<script setup lang="ts">
  import { usePDF } from '@/store/pdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import '@tato30/vue-pdf/style.css'
  import SignaturePad from 'signature_pad'
  import { onMounted, reactive, ref, onBeforeUnmount, computed, watch, nextTick } from 'vue'
  import Vue3Signature from 'vue3-signature'
  import Button from '../ui/button/Button.vue'
  import { useThemeColor } from '@/composables/useThemeColor'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import { RotateCcw, Signature } from 'lucide-vue-next'

  const SIGNATURE_FORMAT = 'image/svg+xml'

  const props = defineProps({
    left: {
      type: String,
      required: false,
      default: '50%'
    },
    top: {
      type: String,
      required: false,
      default: '50%'
    },
    width: {
      type: String,
      required: false,
      default: '500px'
    },
    height: {
      type: String,
      required: false,
      default: '250px'
    },
    zIndex: {
      type: Number,
      required: false,
      default: 1000
    },
    data: {
      type: String,
      required: false,
      default: ''
    },
    isOpen: {
      type: Boolean,
      required: false,
      default: undefined
    }
  })

  const signatureStore = useSignatures()
  const { selectedForm } = usePDF()
  const themeColor = useThemeColor()

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void
    (e: 'save', signatureData: string): void
  }>()

  const { t } = useTranslation({
    en: {
      signingTitle: 'Signing',
      signHere: 'Sign here',
      resetButton: 'Reset',
      cancelButton: 'Cancel',
      addButton: 'Add'
    },
    de: {
      signingTitle: 'Unterschrift',
      signHere: 'Hier unterschreiben',
      resetButton: 'Zurücksetzen',
      cancelButton: 'Abbrechen',
      addButton: 'Hinzufügen'
    }
  })

  let checkInterval: NodeJS.Timeout | null = null

  const state = reactive({
    sig: SignaturePad,
    count: 0,
    option: {
      penColor: 'rgb(0, 0, 0)',
      backgroundColor: 'rgb(255,255,255)'
    },
    disabled: false
  })

  const signature1 = ref<{
    undo: () => void
    clear: () => void
    save: (t: any) => any
    isEmpty: () => boolean
    fromDataURL: (arg0: string) => void
    addWaterMark: (arg0: {
      text: string
      font: string
      style: string
      fillStyle: string
      strokeStyle: string
      x: number
      y: number
      sx: number
      sy: number
    }) => void
    disabled: boolean
    defaultUrl: string
    clearOnResize: boolean
    w: number
    h: number
    sigOption: {
      minWidth: number
      maxWidth: number
      penColor: string
      backgroundColor: string
      throttle: number
      minDistance: number
    }
    waterMark: {
      text: string
      font: string
      style: string
      fillStyle: string
      strokeStyle: string
      x: number
      y: number
      sx: number
      sy: number
    }
  } | null>(null)

  // Track the element that triggered the modal so we can restore focus on close
  const triggerElement = ref<HTMLElement | null>(null)

  const isDialogOpen = computed({
    get: () => {
      return props.isOpen !== undefined ? props.isOpen : signatureStore.showSignModal
    },
    set: (value: boolean) => {
      if (props.isOpen !== undefined) {
        emit('update:isOpen', value)
      }
      // Always sync with store for backward compatibility with save/clear functions
      signatureStore.showSignModal = value
    }
  })

  // Capture trigger element when dialog opens, restore focus when it closes
  watch(isDialogOpen, (isOpen, wasOpen) => {
    if (isOpen && !wasOpen) {
      triggerElement.value = document.activeElement as HTMLElement | null
    } else if (!isOpen && wasOpen) {
      nextTick(() => {
        triggerElement.value?.focus()
        triggerElement.value = null
      })
    }
  })

  // Make isEmpty reactive by using a ref and updating it when signature changes
  const isEmpty = ref(true)

  // Function to check if signature is empty
  function checkIsEmpty(): boolean {
    return signature1.value?.isEmpty() ?? true
  }

  // Update isEmpty whenever signature changes
  function updateIsEmpty() {
    isEmpty.value = checkIsEmpty()
  }

  function update(sel: any) {
    if (!sel) {
      console.warn('No selected signature annotation')
      return
    }

    // Use manualFormId if selectedForm is not available (e.g., in client portal)
    const formId = selectedForm?.documentProperties.id || signatureStore.manualFormId
    const signatureKey = formId + sel.name
    signatureStore.signatures[signatureKey] = {
      data_uri: signature1.value?.save(SIGNATURE_FORMAT),
      annotation_id: sel.id,
      annotation_field_name: sel.name
    }
  }

  function save() {
    const signatureData = signature1.value?.save(SIGNATURE_FORMAT)

    if (!isEmpty.value && signatureData) {
      emit('save', signatureData)
    }

    if (!isEmpty.value && signatureStore.selectedSignatureFieldObject) {
      update(signatureStore.selectedSignatureFieldObject)
    }

    isDialogOpen.value = false
  }

  function clear() {
    signature1.value?.clear()
    updateIsEmpty() // Update isEmpty after clearing
    const sel = signatureStore.selectedSignatureFieldObject
    const formId = selectedForm?.documentProperties.id || signatureStore.manualFormId
    if (!sel || !formId) return

    const signatureKey = formId + sel.name
    if (Object.prototype.hasOwnProperty.call(signatureStore.signatures, signatureKey)) {
      delete signatureStore.signatures[signatureKey]
    }
  }

  function handleCancel() {
    isDialogOpen.value = false
  }

  // Watch for signature changes and update isEmpty
  watch(
    () => signature1.value,
    (newVal) => {
      if (newVal) {
        // Update isEmpty whenever signature pad is available
        updateIsEmpty()

        // Set up a periodic check for signature changes
        checkInterval = setInterval(() => {
          if (signature1.value) {
            const wasEmpty = isEmpty.value
            const isNowEmpty = checkIsEmpty()
            if (wasEmpty !== isNowEmpty) {
              isEmpty.value = isNowEmpty
            }
          }
        }, 100) // Check every 100ms

        // Clean up interval after 10 seconds
        setTimeout(() => {
          if (checkInterval) {
            clearInterval(checkInterval)
            checkInterval = null
          }
        }, 10000)
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    signature1.value?.fromDataURL(props.data)
  })

  onBeforeUnmount(() => {
    signatureStore.cleanup()
    if (checkInterval) {
      clearInterval(checkInterval)
    }
  })
</script>

<template>
  <Dialog
    :open="isDialogOpen"
    @update:open="isDialogOpen = $event"
  >
    <DialogContent
      class="w-full max-w-[609px] gap-6 rounded-lg p-6"
      :mobile-bottom-sheet="true"
      :overlay-class="`z-[${props.zIndex}]`"
      :class="`z-[${props.zIndex}]`"
    >
      <!-- Header -->
      <DialogHeader>
        <DialogTitle class="text-xl font-semibold text-gray-900">
          {{ t('signingTitle') }}
        </DialogTitle>
        <DialogDescription class="sr-only">
          {{ t('signHere') }}
        </DialogDescription>
      </DialogHeader>
      <!-- Signature Area -->
      <div
        class="relative flex h-[145px] flex-col items-center justify-center gap-2 overflow-hidden rounded-lg
          border-2 border-gray-300 bg-white p-4"
      >
        <!-- Placeholder content - shows when no signature -->
        <div
          v-if="isEmpty"
          class="pointer-events-none relative z-10 flex h-6 w-6 items-center justify-center text-gray-500"
        >
          <Signature class="h-5 w-5" />
        </div>
        <span
          v-if="isEmpty"
          class="pointer-events-none relative z-10 text-base font-medium text-gray-500"
        >
          {{ t('signHere') }}
        </span>

        <!-- Signature pad overlay -->
        <div class="absolute inset-0 z-0 h-full w-full">
          <Vue3Signature
            ref="signature1"
            :disabled="state.disabled"
            :sigOption="state.option"
            :w="'100%'"
            :h="'100%'"
            class="h-full w-full"
          />
        </div>
      </div>

      <div class="flex items-center justify-between gap-2 max-sm:flex-col max-sm:gap-3">
        <!-- Reset Button -->
        <Button
          variant="secondary"
          size="sm"
          class="flex h-8 items-center gap-1.5 max-sm:w-full"
          @click="clear"
        >
          <RotateCcw class="h-5 w-5" />
          {{ t('resetButton') }}
        </Button>

        <!-- Right side buttons -->
        <div class="flex items-center gap-2 max-sm:w-full max-sm:justify-between">
          <Button
            variant="secondary"
            size="sm"
            class="h-8 max-sm:flex-1"
            @click="handleCancel"
          >
            {{ t('cancelButton') }}
          </Button>
          <Button
            :class="`bg-${themeColor}-600 hover:bg-${themeColor}-700`"
            size="sm"
            data-cy="SignModalSaveBtn"
            class="flex h-8 items-center gap-1.5 max-sm:flex-1"
            @click="save"
          >
            {{ t('addButton') }}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
  /* Custom styles for the signature pad */
  :deep(.vue-signature-pad) {
    width: 100%;
    height: 100%;
    border: none;
    background-color: transparent;
    position: absolute;
    top: 0;
    left: 0;
  }

  :deep(.vue-signature-pad canvas) {
    border-radius: 0.5rem;
    width: 100% !important;
    height: 100% !important;
  }
</style>
